import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import CreateCampaignDialogFlowRuleErrorsMixin from '@/views/reports/mixins/flow/create-campaign-dialog-flow-rule-errors-mixin';
import CreateCampaignDialogFlowPathErrorsMixin from '@/views/reports/mixins/flow/create-campaign-dialog-flow-path-errors-mixin';
import CreateCampaignDialogFlowOfferErrorsMixin from '@/views/reports/mixins/flow/create-campaign-dialog-flow-offer-errors-mixin';
import CreateCampaignDialogFlowLandingErrorsMixin from '@/views/reports/mixins/flow/create-campaign-dialog-flow-landing-errors-mixin';

export interface PathError {
  hasError: boolean;
}

export interface RuleError {
  hasError: boolean;
  paths: PathError[]
}

@Component
export default class FlowPathErrorsMixin extends mixins(
  CreateCampaignDialogFlowRuleErrorsMixin,
  CreateCampaignDialogFlowPathErrorsMixin,
  CreateCampaignDialogFlowOfferErrorsMixin,
  CreateCampaignDialogFlowLandingErrorsMixin
) {

  @Prop({ default: () => [] as string[] })
  errorConditionPrefix: string[];

  get offset() {
    return this.errorConditionPrefix.length;
  }

  getRulesErrors(rules: any[]) {
    let result: RuleError[] = [];

    for(let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
      const ruleCondition = this.errorConditionPrefix.concat(this.getRuleErrorCondition(ruleIndex));
      const rule = rules[ruleIndex];

      let ruleResult: RuleError = {
        hasError: this.getInvalidFieldsError(this.isNoConditionErrorCondition, ruleCondition) 
          || this.getInvalidFieldsError(this.isAtLeastOneActivePathCondition, ruleCondition) 
          || this.getInvalidFieldsError(this.isNotPathErrorCondition, ruleCondition, true),
        paths: []
      };

      for(let pathIndex = 0; pathIndex < rule.paths.length; pathIndex++) {
        const path = rule.paths[pathIndex];
        const pathResult: PathError = {
          hasError: this.isPathError(ruleIndex, path, pathIndex)
        } 

        ruleResult.paths.push(pathResult);
      }

      result.push(ruleResult);
    }

    return result;
  }

  isPathError(ruleIndex: number, path: any,  pathIndex: number) {
    const pathCondition = this.errorConditionPrefix.concat(this.getPathErrorCondition(ruleIndex, pathIndex));

    if(
      this.getEmptyEntityError(this.emptyLandingsCondition, pathCondition) 
      || this.getEmptyEntityError(this.emptyOffersCondition, pathCondition)
      || this.getInvalidFieldsError(this.pathWeigthErrorCondition, pathCondition)
    ) {
      return true;
    }

    const { offers, landings } = path;

    for(let offerIndex = 0; offerIndex < offers.length; offerIndex++) {
      const offerCondition = this.errorConditionPrefix.concat(this.getOfferErrorCondition(ruleIndex, pathIndex, offerIndex));

      if(
          this.getInvalidFieldsError(this.offerGeneralErrorCondition, offerCondition)
          || this.getInvalidFieldsError(this.offerWeigthErrorCondition, offerCondition)
        ) {
        return true;
      }
    }

    for(let landingIndex = 0; landingIndex < landings.length; landingIndex++) {
      const landingCondition = this.errorConditionPrefix.concat(this.getLandingErrorCondition(ruleIndex, pathIndex, landingIndex));

      if(
          this.getInvalidFieldsError(this.landingGeneralErrorCondition, landingCondition)
          || this.getInvalidFieldsError(this.landingWeigthErrorCondition, landingCondition)
        ) {
        return true;
      }
    }

    return false;
  }

  mergeErrors(a: boolean[], b: boolean[]) {
    const result = a.map((item, index) =>  item || b[index] || false);
    return result;
  }

  processRuleError(ruleIndex: number) {
    const ruleCondition = this.errorConditionPrefix.concat(this.getRuleErrorCondition(ruleIndex));

    let errors: boolean[][] = [
      this.getEmptyEntityErrors([], ruleCondition)
    ];

    return errors.reduce(this.mergeErrors, this.errorsRaw.map(() => false));
  }

  processPathError(ruleIndex: number, pathIndex: number) {
    const pathCondition = this.errorConditionPrefix.concat(this.getPathErrorCondition(ruleIndex, pathIndex));

    let errors: boolean[][] = [
      this.getEmptyEntityErrors([], pathCondition)
    ];

    return errors.reduce(this.mergeErrors, this.errorsRaw.map(() => false));
  }

  processOfferError(ruleIndex: number, pathIndex: number, offerIndex: number) {
    const offerCondition = this.errorConditionPrefix.concat(this.getOfferErrorCondition(ruleIndex, pathIndex, offerIndex));

    let errors: boolean[][] = [
      this.getEmptyEntityErrors([], offerCondition)
    ];

    return errors.reduce(this.mergeErrors, this.errorsRaw.map(() => false));
  }

  processLandingError(ruleIndex: number, pathIndex: number, landingIndex: number) {
    const landingCondition = this.errorConditionPrefix.concat(this.getLandingErrorCondition(ruleIndex, pathIndex, landingIndex));

    let errors: boolean[][] = [
      this.getEmptyEntityErrors([], landingCondition)
    ];

    return errors.reduce(this.mergeErrors, this.errorsRaw.map(() => false));
  }

  fixPathErrorIndexes(ruleIndex: number, pathIndex: number, pathErrors:  boolean[]) {
    const ruleErrors = this.processRuleError(ruleIndex);
    return this.errorsRaw
      .map((item: any, index: number) => {
        if(!ruleErrors[index]) {
          return item;
        }

        if(item[this.offset + 3] > pathIndex + 1) {
          let result = item.slice(0);
          result[this.offset + 3]--;
          return result;
        }

        return item;
      })
      .filter((item: any, index: number) => !pathErrors[index]);
  }

  fixRuleErrorIndexes(ruleIndex: number, ruleErrors:  boolean[]) {
    return this.errorsRaw
      .map((item: any, index: number) => {
        if(item[this.offset + 1] > ruleIndex + 1) {
          let result = item.slice(0);
          result[this.offset + 1]--;
          return result;
        }

        return item;
      })
      .filter((item: any, index: number) => !ruleErrors[index]);
  }

  fixOfferErrorIndexes(ruleIndex: number, pathIndex: number, offerIndex: number, offerErrors:  boolean[]) {
    return this.fixPathSubEntitiesErrorIndexes(ruleIndex, pathIndex, offerIndex, offerErrors);
  }

  fixLandingErrorIndexes(ruleIndex: number, pathIndex: number, landingIndex: number, offerErrors:  boolean[]) {
    return this.fixPathSubEntitiesErrorIndexes(ruleIndex, pathIndex, landingIndex, offerErrors);
  }

  fixPathSubEntitiesErrorIndexes(ruleIndex: number, pathIndex: number, subEntityIndex: number, offerErrors:  boolean[]) {
    const pathErrors = this.processPathError(ruleIndex, pathIndex);

    return this.errorsRaw
      .map((item: any, index: number) => {
        if(!pathErrors[index]) {
          return item;
        }

        if(item[this.offset + 5] > subEntityIndex + 1) {
          let result = item.slice(0);
          result[this.offset + 5]--;
          return result;
        }

        return item;
      })
      .filter((item: any, index: number) => !offerErrors[index]);
  }
}

